<script setup>
import { ref } from 'vue'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import ProductRail from '@/Components/Public/ProductRail.vue'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const props = defineProps({
  tour:      { type: Object, required: true },
  base_path: { type: String, default: '/tours' },
  related:   { type: Array, default: () => [] }
})

const activeIdx = ref(0)
const photos = props.tour.photo_urls?.length ? props.tour.photo_urls : []
</script>

<template>
  <Head>
    <title>{{ tour.title }} · {{ t('public.head_title') }}</title>
    <meta name="description" :content="tour.summary || tour.description">
  </Head>

  <BrandHero kind="tour" :eyebrow="t(`vendor_portal.tour.categories.${tour.category}`)" :title="tour.title">
    <template #top>
      <a :href="base_path" class="bhero-back">← {{ t('public.tours.back') }}</a>
    </template>
    <p v-if="tour.summary" class="bhero-lede">{{ tour.summary }}</p>
    <span v-if="tour.verified" class="bhero-verified">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" /></svg>{{ t('public.verified') }}
    </span>
    <p class="bhero-price"><span class="fw-value">{{ tour.price_adult }}</span> <span class="bhero-per">/ {{ Math.round(tour.duration_minutes / 60) }}h</span></p>
    <div v-if="tour.contact_intents && Object.keys(tour.contact_intents).length" class="bhero-ctas">
      <a
        v-for="(url, platform, idx) in tour.contact_intents"
        :key="platform"
        :class="['bhero-cta', idx === 0 ? 'bhero-cta--solid' : 'bhero-cta--ghost']"
        :href="url"
        target="_blank"
        rel="noopener"
      >{{ t('public.write_on', { platform: platform.charAt(0).toUpperCase() + platform.slice(1) }) }}</a>
    </div>
  </BrandHero>

  <section class="body">
    <div class="body__inner">
      <div class="layout">
        <div class="gallery">
          <div class="gallery__main">
            <img v-if="photos[activeIdx]" :src="photos[activeIdx]" :alt="tour.title">
            <CatalogPlaceholder v-else kind="tour" :category="tour.category" />
          </div>
          <ul v-if="photos.length > 1" class="thumbs">
            <li v-for="(p, i) in photos" :key="p">
              <button :class="['thumb', i === activeIdx && 'is-active']" @click="activeIdx = i">
                <img :src="p" alt="">
              </button>
            </li>
          </ul>
        </div>

        <div class="info">
          <section class="card">
            <h2 class="section-title">{{ t('public.tours.sections.about') }}</h2>
            <p class="prose">{{ tour.description }}</p>
          </section>

          <section v-if="tour.inclusions" class="card">
            <h2 class="section-title">{{ t('public.tours.sections.included') }}</h2>
            <p class="prose">{{ tour.inclusions }}</p>
          </section>

          <section class="card">
            <h2 class="section-title">{{ t('public.tours.sections.details') }}</h2>
            <dl class="dl">
              <div><dt>{{ t('public.tours.fields.duration') }}</dt><dd>{{ Math.round(tour.duration_minutes / 60) }}h</dd></div>
              <div><dt>{{ t('public.tours.fields.price_adult') }}</dt><dd class="dl__price">{{ tour.price_adult }}</dd></div>
              <div v-if="tour.price_child"><dt>{{ t('public.tours.fields.price_child') }}</dt><dd>{{ tour.price_child }}</dd></div>
              <div v-if="tour.max_group_size"><dt>{{ t('public.tours.fields.max_group_size') }}</dt><dd>{{ tour.max_group_size }}</dd></div>
              <div v-if="tour.languages_offered"><dt>{{ t('public.tours.fields.languages_offered') }}</dt><dd>{{ tour.languages_offered }}</dd></div>
              <div v-if="tour.meeting_point_address"><dt>{{ t('public.tours.fields.meeting_point') }}</dt><dd>{{ tour.meeting_point_address }}</dd></div>
              <div v-if="tour.meeting_point_description"><dt>{{ t('public.tours.fields.meeting_point_description') }}</dt><dd class="prose-inline">{{ tour.meeting_point_description }}</dd></div>
            </dl>
          </section>
        </div>
      </div>
    </div>
  </section>

  <section
    v-if="related.length"
    class="related"
  >
    <div class="related__inner">
      <ProductRail
        :title="t('public.tours.related')"
        kind="tour"
        :items="related"
        :item-base="base_path"
        :view-all-href="base_path"
      />
    </div>
  </section>
</template>

<style scoped>
.bhero-back { display: inline-block; margin-bottom: 1rem; color: inherit; opacity: 0.85; text-decoration: none; font-size: 0.95rem; }
.bhero-back:hover { opacity: 1; }
.bhero-lede { margin: 0.9rem 0 0; font-size: clamp(1.05rem, 2vw, 1.25rem); line-height: 1.5; max-width: 52ch; opacity: 0.94; }
.bhero-verified {
  display: inline-flex; align-items: center; gap: 0.35rem; margin: 1rem 0 0;
  padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.85rem; font-weight: 700;
  background: color-mix(in oklch, currentColor 16%, transparent);
  border: 1px solid color-mix(in oklch, currentColor 35%, transparent);
}
.bhero-verified svg { width: 1rem; height: 1rem; }
.bhero-price { margin: 1.1rem 0 0; font-size: clamp(1.6rem, 3vw, 2.1rem); }
.bhero-per { font-size: 1rem; opacity: 0.85; }
.bhero-ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 1.5rem; }
.bhero-cta {
  display: inline-flex; align-items: center; padding: 0.8rem 1.4rem; border-radius: 999px;
  font-family: var(--font-display, var(--display)); font-weight: 700; font-size: 1rem; text-decoration: none;
}
.bhero-cta--solid { background: var(--ink); color: #fff; }
.bhero-cta--ghost { background: color-mix(in oklch, #fff 14%, transparent); color: inherit; border: 1px solid color-mix(in oklch, #fff 45%, transparent); }

.body { background: var(--bg); }
.body__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; }
.layout { display: grid; grid-template-columns: 1fr; gap: 2rem; }
@media (min-width: 880px) { .layout { grid-template-columns: 1.4fr 1fr; gap: 2.5rem; } }
.gallery__main { aspect-ratio: 4 / 3; background: var(--surfaceAlt); border-radius: 1rem; overflow: hidden; display: grid; place-items: center; }
.gallery__main img { width: 100%; height: 100%; object-fit: cover; }
.thumbs { list-style: none; padding: 0; margin: 0.75rem 0 0; display: flex; gap: 0.5rem; overflow-x: auto; }
.thumb { width: 5rem; height: 4rem; padding: 0; border: 2px solid transparent; border-radius: 0.5rem; overflow: hidden; cursor: pointer; background: var(--surfaceAlt); }
.thumb.is-active { border-color: var(--accent); }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.info { display: grid; gap: 1rem; }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.section-title { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.75rem; }
.prose { color: var(--inkSoft); line-height: 1.6; white-space: pre-wrap; margin: 0; }
.prose-inline { white-space: pre-wrap; }
.dl { display: grid; gap: 0.55rem; margin: 0; }
.dl > div { display: grid; grid-template-columns: 9rem 1fr; gap: 0.5rem; align-items: baseline; }
.dl dt { color: var(--muted); font-size: 0.875rem; }
.dl dd { margin: 0; color: var(--ink); font-size: 1rem; }
.dl__price { color: var(--accent); font-weight: 700; }
.related { background: var(--surface); border-top: 1px solid var(--line); }
.related__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem); }
</style>
